import { useUser } from '@app/context/UserContext';
import { pickLatest } from '@app/utils/pickLatest';
import { NotificationModal, Spinner } from '@components/ui';
import { useDuplicateQuestionnaireMutation } from '@dieterApi/questionnaire/useDuplicateQuestionnaireMutation';
import { useQuestionnairesQuery } from '@dieterApi/questionnaire/useQuestionnairesQuery';
import { useOtherTenantsQuestionnairesQuery } from '@dieterApi/user/useOtherTenantsQuestionnairesQuery';
import { UserApplicationQuestionnaires } from '@dieterApi/user/useUserQuery';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ModalProps, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface DuplicateModalProps extends Omit<ModalProps, 'children'> {
  app: UserApplicationQuestionnaires;
}

export const DuplicateModal = ({ open, onClose, app, ...props }: DuplicateModalProps) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const [duplicateQuestionnaire, { loading }] = useDuplicateQuestionnaireMutation();
  const [queryQuestionnaires, { data: questionnairesData, loading: questionnairesLoading }] = useQuestionnairesQuery();
  const { data: otherTenantsData, loading: otherTenantsLoading } = useOtherTenantsQuestionnairesQuery();

  // choose latest questionnaire
  const questionnaire = pickLatest(app.questionnaires);

  const [loadingForCompany, setLoadingForCompany] = useState<string | null>(null);

  const otherTenants = otherTenantsData?.getUser?.companies.filter((company) => company.id !== user?.company?.id);

  const handleDuplicate = (companyId: string) => {
    if (questionnaire) {
      setLoadingForCompany(companyId);
      duplicateQuestionnaire({
        variables: {
          localQuestionnaireId: questionnaire?.id,
          companyId,
        },
      });
    }
  };

  useEffect(() => {
    if (!loading && !questionnairesLoading) {
      setLoadingForCompany(null);
    }
  }, [loading, questionnairesLoading]);

  useEffect(() => {
    if (otherTenants?.length && questionnaire?.id) {
      const otherIds = otherTenants
        ?.map((company) => {
          // find out if the other company already has an instance of this app
          const existingQuestionnaires = company.questionnaires?.filter((q) => q.application.id === app.id);
          const existingLatestQuestionnaire =
            existingQuestionnaires.length > 0
              ? existingQuestionnaires?.reduce((prev, current) =>
                  prev?.createdAt > current.createdAt ? prev : current
                )
              : null;
          return existingLatestQuestionnaire?.id;
        })
        .filter(Boolean) as string[];

      queryQuestionnaires({
        variables: {
          ids: [...otherIds, questionnaire.id],
        },
      });
    }
    // TODO: THIS LOOPS
  }, [otherTenants?.flatMap((c) => c.questionnaires.flatMap((q) => q.id)).join('')]);

  return (
    <NotificationModal open={open} onClose={onClose} {...props}>
      <div className="dtDuplicateModal flex flex-col gap-5">
        <div className="text-lg font-medium">{t('dashboard.topic.duplicate_modal.title')}</div>
        {otherTenants?.map((company) => {
          // find out if the other company already has an instance of this app
          const existingQuestionnaires = company.questionnaires?.filter((q) => q.application.id === app.id);
          const existingLatestQuestionnaire =
            existingQuestionnaires.length > 0
              ? existingQuestionnaires?.reduce((prev, current) =>
                  prev?.createdAt > current.createdAt ? prev : current
                )
              : null;

          const existingProgress = questionnairesData?.getQuestionnaires.find(
            (q) => q.id === existingLatestQuestionnaire?.id
          )?.progressHash;
          const currentProgress = questionnairesData?.getQuestionnaires.find(
            (q) => q.id === questionnaire?.id
          )?.progressHash;
          const sameSame = existingProgress === currentProgress;

          return (
            <div key={company.id} className="flex justify-between items-center">
              <div>{company.name}</div>
              {loadingForCompany === company.id ? (
                <Spinner size="small" />
              ) : existingLatestQuestionnaire ? (
                sameSame ? (
                  <CheckCircleOutlineIcon color="success" />
                ) : (
                  <div className="flex gap-[10px]">
                    <Tooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={5000}
                      title="Es existiert bereits eine Fragebogeninstanz mit abweichenden Antworten"
                    >
                      <WarningAmberIcon color="warning" />
                    </Tooltip>
                    <Link to="#" onClick={() => handleDuplicate(company.id)}>
                      {t('common.overwrite')}
                    </Link>
                  </div>
                )
              ) : (
                <Link to="#" onClick={() => handleDuplicate(company.id)}>
                  {t('common.accept')}
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </NotificationModal>
  );
};
